"use client";

import { RecommendationProps } from "@/app/ec/product/recommendations/recommendations";
import SidebarRecommendation from "@/app/ec/product/recommendations/sidebarRecommendation";
import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  RecommendationsSliceState,
  generateRecommendationsCacheKey,
  getRecommendations,
} from "@/store/recommendationsSlice";
import { getBestPrices } from "@/store/searchSlice";
import { AppState } from "@/store/store";
import { createSelector } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useInView } from "react-intersection-observer";
import styles from "./sidebarRecommendations.module.scss";

function SidebarRecommendations({
  objectIDs = [],
  modelParams = {},
  model,
  title,
  maxRecommendations = 5,
}: RecommendationProps) {
  const { ref, inView /* , entry */ } = useInView({
    rootMargin: "-200px 0px 0px 0px",
    triggerOnce: true,
  });
  const { zipCode } = useLocationDetails();
  const dispatch = useAppDispatch();

  const selectRecommendations = useMemo(() => {
    return createSelector(
      [
        (state: AppState) => state.recommendationsSlice.recommendations,
        (state: AppState, cacheKey: string) => cacheKey,
      ],
      (recommendations: RecommendationsSliceState["recommendations"], cacheKey: string) => recommendations[cacheKey],
    );
  }, []);
  const recommendationsCacheKey = useMemo(() => {
    const key = generateRecommendationsCacheKey(model, objectIDs, { maxRecommendations, ...modelParams });
    return key;
  }, [model, objectIDs, maxRecommendations, modelParams]);

  const recommendations = useAppSelector((state) => selectRecommendations(state, recommendationsCacheKey));

  useEffect(() => {
    if (recommendations && recommendations.loadingState === "complete" && recommendations.hits.length > 0) {
      const mpIds = recommendations.hits.map((hit): number => hit.mp_id);
      dispatch(getBestPrices({ mpIds: mpIds, postalCode: zipCode }));
    }
  }, [recommendations, zipCode, dispatch]);

  const isLoading = useMemo(() => {
    if (!recommendations) {
      return true;
    }
    return recommendations.loadingState === "loading";
  }, [recommendations]);

  useEffect(() => {
    if (inView && !recommendations) {
      dispatch(getRecommendations({ model, objectIDs, maxRecommendations, ...modelParams }));
    }
  }, [dispatch, recommendations, inView, model, objectIDs, maxRecommendations, modelParams]);

  const factorIdChangeCallback = useCallback(() => {
    const factorIdDiv = document.getElementById("factorIdDiv");
    if (
      factorIdDiv &&
      recommendations &&
      recommendations.loadingState === "complete" &&
      recommendations.hits.length > 0
    ) {
      Cookies.set("buyBoxFactorIdExperiment", factorIdDiv.innerHTML, { expires: 1 / 24 });
      const mpIds = recommendations.hits.map((hit): number => hit.mp_id);
      dispatch(
        getBestPrices({
          mpIds: mpIds,
          postalCode: zipCode,
          additionalCacheKey: `factorId:${factorIdDiv.innerHTML}`,
        }),
      );
    }
  }, [dispatch, zipCode, recommendations]);

  useEffect(() => {
    window.addEventListener("factorIDChange", factorIdChangeCallback);
    const intervalFactorIdChecker = setInterval(() => {
      const factorIdDiv = document.getElementById("factorIdDiv");
      if (
        factorIdDiv &&
        recommendations &&
        recommendations.loadingState === "complete" &&
        recommendations.hits.length > 0
      ) {
        const validCookie = Cookies.get("buyBoxFactorIdExperiment");
        if (!validCookie) {
          const mpIds = recommendations.hits.map((hit): number => hit.mp_id);
          Cookies.set("buyBoxFactorIdExperiment", factorIdDiv.innerHTML, { expires: 1 / 24 });
          dispatch(
            getBestPrices({
              mpIds: mpIds,
              postalCode: zipCode,
              additionalCacheKey: `factorId:${factorIdDiv.innerHTML}`,
            }),
          );
        }
      }
    }, 1000 * 60);
    return () => {
      window.removeEventListener("factorIDChange", factorIdChangeCallback);
      clearInterval(intervalFactorIdChecker);
    };
  }, [dispatch, factorIdChangeCallback, zipCode, recommendations]);

  const loadingRecommendations = useMemo(() => {
    const max = 5;
    const loadingRecommendations = [];
    for (let index = 0; index < max; index++) {
      loadingRecommendations.push("");
    }
    return loadingRecommendations;
  }, []);

  if (!isLoading && !recommendations?.hits.length) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<></>}>
      <div ref={ref} className={styles.SidebarRecommendations}>
        <div className={`${styles.sectionTitle} ${isLoading ? styles.loadingTitle : ""}`}>{title}</div>
        <div className={styles.list}>
          {isLoading &&
            loadingRecommendations.map((value, index) => {
              return <SidebarRecommendation key={index} isLoadingRecommendation={true} />;
            })}
          {recommendations?.hits.map((recommendation) => {
            return (
              <SidebarRecommendation
                key={recommendation.mp_id}
                mpId={recommendation.mp_id}
                title={recommendation.title}
                brandName={recommendation.brand}
                imageLink={recommendation.image_link}
                detailLink={recommendation.link}
                retailPrice={recommendation.retail_price}
                bestPrice={recommendation.price}
                numberOfRatings={recommendation.number_of_general_ratings}
                rating={recommendation.general_rating}
              />
            );
          })}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default SidebarRecommendations;
